import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  Button,
  Divider,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { showGlobalToast } from "../../../Components/ToastProvider";
import { useSendApplyMutation } from "../../../Store/api/rtk";
import VerificationModal from "../../../Components/VerificationModal";

import PersonalInfoSection from "./PersonalInfoSection";
import JobInfoSection from "./JobInfoSection";
import EducationSection from "./EducationSection";
import WorkExperienceSection from "./WorkExperienceSection";
import LanguageSection from "./LanguageSection";

const STEPS = [
  {
    label: "اطلاعات شخصی",
    fields: [
      "first_name",
      "last_name",
      "father_name",
      "national_code",
      "phone_number",
      "address",
      "birth_date",
      "gender",
      "military_service",
    ],
  },
  {
    label: "اطلاعات شغلی",
    fields: [
      "position_applied",
      "work_type",
      "expected_salary",
      "years_of_experience",
      "skills",
      "familiarity_source",
      "linkedin_url",
      "portfolio_url",
      "cover_letter",
    ],
  },
  {
    label: "تحصیلات",
    fields: [
      "education_level",
      "field_of_study",
      "university_or_institution",
      "education_start_date",
      "education_end_date",
      "is_currently_studying",
      "gpa",
    ],
  },
  {
    label: "سوابق کاری",
    fields: ["work_experiences"],
  },
  {
    label: "زبان‌ها",
    fields: ["languages"],
  },
];

// 1. Helpers to check work experience state
const isWorkExpEmpty = (exp) =>
  !exp?.company_name?.trim() &&
  !exp?.job_title?.trim() &&
  !exp?.start_date &&
  !exp?.end_date &&
  !exp?.reason_for_leaving?.trim();

const isWorkExpComplete = (exp) =>
  Boolean(
    exp?.company_name?.trim() &&
    exp?.job_title?.trim() &&
    exp?.start_date &&
    (exp?.is_current_job || exp?.end_date),
  );

const isLangEmpty = (lang) =>
  !lang?.language_name?.trim() && !lang?.overall_level;

const isLangComplete = (lang) =>
  Boolean(lang?.language_name?.trim() && lang?.overall_level);

// Form validation schema
const validationSchema = yup.object().shape({
  first_name: yup.string().required("نام الزامی است"),
  last_name: yup.string().required("نام خانوادگی الزامی است"),
  father_name: yup.string().required("نام پدر الزامی است"),
  national_code: yup
    .string()
    .required("کد ملی الزامی است")
    .length(10, "کد ملی باید ۱۰ رقم باشد")
    .matches(/^[0-9]+$/, "کد ملی باید فقط شامل اعداد باشد"),
  phone_number: yup.string().required("شماره تماس الزامی است"),
  address: yup.string().required("آدرس الزامی است"),
  birth_date: yup.number().required("تاریخ تولد الزامی است"),
  gender: yup.string().required("جنسیت الزامی است"),
  military_service: yup.string().when("gender", {
    is: "male",
    then: (schema) => schema.required("وضعیت سربازی الزامی است"),
    otherwise: (schema) => schema.nullable(),
  }),
  email: yup.string().email("فرمت ایمیل صحیح نیست").nullable(),
  position_applied: yup.string().required("موقعیت شغلی الزامی است"),
  work_type: yup.string().required("نوع همکاری الزامی است"),
  expected_salary: yup.number().nullable(),
  years_of_experience: yup.number().nullable(),
  skills: yup.array().of(yup.string()),
  familiarity_source: yup.string().nullable(),
  linkedin_url: yup.string().url("لینک نامعتبر است").nullable(),
  portfolio_url: yup.string().url("لینک نامعتبر است").nullable(),
  cover_letter: yup.string().nullable(),
  education_level: yup.string().required("مقطع تحصیلی الزامی است"),
  field_of_study: yup.string().required("رشته تحصیلی الزامی است"),
  university_or_institution: yup.string().nullable(),
  education_start_date: yup.number().nullable(),
  education_end_date: yup.number().nullable(),
  is_currently_studying: yup.boolean().default(false),
  gpa: yup.number().nullable(),
  // Validate only if the card is not completely empty
  work_experiences: yup.array().of(
    yup.lazy((item) => {
      if (isWorkExpEmpty(item)) {
        return yup.object().nullable();
      }
      return yup.object().shape({
        company_name: yup.string().trim().required("نام شرکت الزامی است"),
        job_title: yup.string().trim().required("سمت شغلی الزامی است"),
        start_date: yup.number().nullable().required("تاریخ شروع الزامی است"),
        is_current_job: yup.boolean().default(false),
        end_date: yup
          .number()
          .nullable()
          .when("is_current_job", {
            is: false,
            then: (schema) => schema.required("تاریخ پایان الزامی است"),
            otherwise: (schema) => schema.nullable(),
          }),
        reason_for_leaving: yup.string().nullable(),
      });
    }),
  ),
  languages: yup.array().of(
    yup.lazy((item) => {
      if (isLangEmpty(item)) {
        return yup.object().nullable();
      }
      return yup.object().shape({
        language_name: yup.string().trim().required("نام زبان الزامی است"),
        overall_level: yup.string().required("سطح تسلط الزامی است"),
        reading_level: yup.string().nullable(),
        writing_level: yup.string().nullable(),
        speaking_level: yup.string().nullable(),
        listening_level: yup.string().nullable(),
        has_certificate: yup.boolean().default(false),
        certificate_name: yup.string().nullable(),
        certificate_score: yup.string().nullable(),
      });
    }),
  ),
  willing_to_relocate: yup.boolean().default(false),
});

const initialValues = {
  first_name: "",
  last_name: "",
  father_name: "",
  national_code: "",
  phone_number: "",
  address: "",
  email: "",
  birth_date: null,
  gender: "not_specified",
  military_service: "not_applicable",
  position_applied: "",
  work_type: "onsite",
  expected_salary: "",
  years_of_experience: "",
  familiarity_source: "",
  linkedin_url: "",
  portfolio_url: "",
  cover_letter: "",
  skills: [],
  education_level: "",
  field_of_study: "",
  university_or_institution: "",
  education_start_date: null,
  education_end_date: null,
  is_currently_studying: false,
  gpa: null,
  work_experiences: [],
  languages: [],
  willing_to_relocate: false,
};

export default function EmploymentForm() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeStep, setActiveStep] = useState(0);
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [tmpToken, setTmpToken] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [resumeFile, setResumeFile] = useState(null);
  const [submitEmployment, { isLoading: isSubmitting }] =
    useSendApplyMutation();

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const workExperiences = values?.work_experiences ?? [];
      const nonEmptyWork = workExperiences.filter(
        (exp) => !isWorkExpEmpty(exp),
      );
      const hasInvalidWork = nonEmptyWork.some(
        (exp) => !isWorkExpComplete(exp),
      );
      if (hasInvalidWork) {
        showGlobalToast(
          "لطفاً فیلدهای اجباری سوابق کاری را تکمیل یا سابقه ناقص را حذف کنید",
          "warning",
        );
        return;
      }

      const languages = values?.languages ?? [];
      const hasInvalidLang = languages.some(
        (lang) => !lang?.language_name || !lang?.overall_level,
      );
      if (languages.length > 0 && hasInvalidLang) {
        showGlobalToast("لطفاً تمام فیلدهای زبان را تکمیل کنید", "warning");
        return;
      }

      try {
        const payload = {
          ...values,
          email: values?.email || null,
          linkedin_url: values?.linkedin_url || null,
          portfolio_url: values?.portfolio_url || null,
          cover_letter: values?.cover_letter || null,
          familiarity_source: values?.familiarity_source || null,
          expected_salary: values?.expected_salary
            ? Number(values.expected_salary)
            : null,
          years_of_experience: values?.years_of_experience
            ? Number(values.years_of_experience)
            : null,
          gender: values?.gender || "not_specified",
          military_service: values?.military_service || "not_applicable",
          willing_to_relocate: Boolean(values?.willing_to_relocate),

          education_history: {
            education_level: values?.education_level,
            field_of_study: values?.field_of_study,
            university_or_institution:
              values?.university_or_institution || null,
            start_date: values?.education_start_date
              ? Math.floor(values.education_start_date / 1000)
              : null,
            end_date: values?.education_end_date
              ? Math.floor(values.education_end_date / 1000)
              : null,
            is_currently_studying: Boolean(values?.is_currently_studying),
            gpa: values?.gpa || null,
          },

          secondary_languages: (values?.languages ?? [])
            .filter((lang) => isLangComplete(lang))
            .map((lang) => ({
              language_name: lang?.language_name,
              overall_level: lang?.overall_level,
              ...(lang?.overall_level !== "native" && {
                reading_level: lang?.reading_level || null,
                writing_level: lang?.writing_level || null,
                speaking_level: lang?.speaking_level || null,
                listening_level: lang?.listening_level || null,
              }),
              has_certificate: Boolean(lang?.has_certificate),
              certificate_name: lang?.has_certificate
                ? lang?.certificate_name
                : null,
              certificate_score: lang?.has_certificate
                ? lang?.certificate_score
                : null,
            })),

          past_experiences: (values?.work_experiences ?? [])
            .filter((exp) => exp?.company_name && exp?.job_title)
            .map((exp) => ({
              company_name: exp?.company_name,
              job_title: exp?.job_title,
              start_date: exp?.start_date
                ? Math.floor(exp.start_date / 1000)
                : null,
              end_date: exp?.end_date ? Math.floor(exp.end_date / 1000) : null,
              is_current_job: Boolean(exp?.is_current_job),
              reason_for_leaving: exp?.reason_for_leaving || null,
            })),

          resume_file: resumeFile,
          Opinion: null,
        };

        const res = await submitEmployment(payload).unwrap();

        if (res?.status === "ok" || res?.data?.temp_token) {
          setTmpToken(res?.data?.temp_token);
          setUserPhone(values?.phone_number);
          setIsVerifyOpen(true);
          showGlobalToast("درخواست شما با موفقیت ثبت شد", "success");
        }
      } catch (error) {
        const errorMessage = error?.data?.message || "خطا در ثبت درخواست";
        showGlobalToast(errorMessage, "error");
      }
    },
  });

  // Step-by-step validation logic
  const validateStep = async (stepIndex) => {
    const step = STEPS?.[stepIndex];
    const touchedFields = {};

    step?.fields?.forEach((field) => {
      if (field === "work_experiences") {
        touchedFields.work_experiences = (
          formik?.values?.work_experiences ?? []
        ).map(() => ({
          company_name: true,
          job_title: true,
          start_date: true,
          end_date: true,
        }));
      } else if (field === "languages") {
        touchedFields.languages = (formik?.values?.languages ?? []).map(() => ({
          language_name: true,
          overall_level: true,
        }));
      } else {
        touchedFields[field] = true;
      }
    });

    await formik.setTouched({ ...formik.touched, ...touchedFields }, false);
    const errors = await formik.validateForm();

    const stepErrors = {};
    step?.fields?.forEach((field) => {
      if (errors?.[field]) {
        stepErrors[field] = errors[field];
      }
    });

    if (stepIndex === 3) {
      const workExps = formik?.values?.work_experiences ?? [];
      // Only block if the user started filling a card but left required fields empty
      const invalidIndex = workExps.findIndex(
        (exp) => !isWorkExpEmpty(exp) && !isWorkExpComplete(exp),
      );
      if (invalidIndex !== -1) {
        showGlobalToast(
          `لطفاً اطلاعات سابقه کاری شماره ${invalidIndex + 1} را تکمیل یا آن را حذف کنید`,
          "warning",
        );
        return false;
      }
    }

    if (stepIndex === 4) {
      const langs = formik?.values?.languages ?? [];
      const invalidIndex = langs.findIndex(
        (lang) => !isLangEmpty(lang) && !isLangComplete(lang),
      );
      if (invalidIndex !== -1) {
        showGlobalToast(
          `لطفاً اطلاعات زبان شماره ${invalidIndex + 1} را تکمیل یا آن را حذف کنید`,
          "warning",
        );
        return false;
      }
    }

    if (Object.keys(stepErrors).length > 0) {
      const firstError = Object.values(stepErrors)[0];
      if (typeof firstError === "string") {
        showGlobalToast(firstError, "warning");
      }
      return false;
    }

    return true;
  };

  const handleNext = async () => {
    const isValid = await validateStep(activeStep);
    if (!isValid) return;

    if (activeStep === STEPS.length - 1) {
      formik.handleSubmit();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const goToStep = (step) => {
    if (step <= activeStep) {
      setActiveStep(step);
    } else {
      showGlobalToast("لطفاً مراحل قبلی را کامل کنید", "warning");
    }
  };

  const addWorkExperience = () => {
    const current = formik?.values?.work_experiences ?? [];

    // Check if the previous work experience is completed before adding a new one
    if (current.length > 0) {
      const lastIndex = current.length - 1;
      const lastExp = current[lastIndex];

      if (!isWorkExpComplete(lastExp)) {
        const currentTouched = formik?.touched?.work_experiences ?? [];
        const updatedTouched = [...currentTouched];
        updatedTouched[lastIndex] = {
          company_name: true,
          job_title: true,
          start_date: true,
          end_date: !lastExp?.is_current_job,
        };

        formik?.setFieldTouched("work_experiences", updatedTouched, true);
        showGlobalToast(
          "لطفاً ابتدا اطلاعات اجباری سابقه کاری فعلی را تکمیل کنید",
          "warning",
        );
        return;
      }
    }

    formik?.setFieldValue("work_experiences", [
      ...current,
      {
        company_name: "",
        job_title: "",
        start_date: null,
        end_date: null,
        is_current_job: false,
        reason_for_leaving: "",
      },
    ]);
  };

  const removeWorkExperience = (index) => {
    const current = formik?.values?.work_experiences ?? [];
    formik?.setFieldValue(
      "work_experiences",
      current.filter((_, i) => i !== index),
    );
  };

  const addLanguage = () => {
    const current = formik?.values?.languages ?? [];

    if (current.length > 0) {
      const lastIndex = current.length - 1;
      const lastLang = current[lastIndex];

      if (!isLangComplete(lastLang)) {
        const currentTouched = formik?.touched?.languages ?? [];
        const updatedTouched = [...currentTouched];
        updatedTouched[lastIndex] = {
          language_name: true,
          overall_level: true,
        };

        formik?.setFieldTouched("languages", updatedTouched, true);
        showGlobalToast(
          "لطفاً ابتدا اطلاعات اجباری زبان قبلی را تکمیل کنید",
          "warning",
        );
        return;
      }
    }

    formik?.setFieldValue("languages", [
      ...current,
      {
        language_name: "",
        overall_level: "",
        reading_level: null,
        writing_level: null,
        speaking_level: null,
        listening_level: null,
        has_certificate: false,
        certificate_name: "",
        certificate_score: "",
      },
    ]);
  };

  const removeLanguage = (index) => {
    const current = formik?.values?.languages ?? [];
    formik?.setFieldValue(
      "languages",
      current.filter((_, i) => i !== index),
    );
  };
  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return <PersonalInfoSection formik={formik} />;
      case 1:
        return <JobInfoSection formik={formik} />;
      case 2:
        return <EducationSection formik={formik} />;
      case 3:
        return (
          <WorkExperienceSection
            formik={formik}
            onAdd={addWorkExperience}
            onRemove={removeWorkExperience}
          />
        );
      case 4:
        return (
          <LanguageSection
            formik={formik}
            onAdd={addLanguage}
            onRemove={removeLanguage}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ py: { xs: 10, md: 15 }, position: "relative" }}>
      <Container maxWidth="xl">
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            p: { xs: 3, md: 6 },
            borderRadius: "24px",
            bgcolor: "rgba(10, 10, 10, 0.54)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(255, 255, 255, 0.08)",
            borderTop: "1px solid rgba(0, 255, 204, 0.4)",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
            position: "relative",
            overflow: "hidden",
            "& *": { direction: "rtl !important" },
          }}
          role="form"
          aria-label="فرم درخواست استخدام"
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "60%",
              height: "200px",
              background:
                "radial-gradient(ellipse, rgba(0,255,204,0.12) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
            aria-hidden="true"
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <AssignmentOutlinedIcon
              sx={{ color: "#00ffcc", fontSize: 32 }}
              aria-hidden="true"
            />
            <Typography
              sx={{
                fontSize: { xs: 20, md: 24 },
                fontWeight: 900,
                color: "#fff",
              }}
              component="h1"
            >
              فرم درخواست استخدام
            </Typography>
          </Box>
          <Typography
            sx={{ opacity: 0.6, fontSize: 14, color: "#fff", mb: 5 }}
            component="p"
          >
            لطفاً اطلاعات هویتی و تخصصی خود را با دقت وارد کنید تا رزومه شما در
            سریع‌ترین زمان ممکن بررسی شود.
          </Typography>

          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{
              mb: 5,
              direction: "rtl !important",
              "& *": { direction: "rtl !important" },
              "& .MuiStepLabel-label": { fontSize: "0.8rem" },
              "& .MuiStepLabel-label.Mui-active": { color: "#01a47c" },
              "& .MuiStepIcon-root.Mui-active": { color: "#01a47c" },
              "& .MuiStepIcon-root.Mui-completed": { color: "#01a47c" },
              "& .MuiStepConnector-root": {
                color: "red !important",
                right: "calc(-50% + 20px)",
                left: "calc(50% + 20px)",
              },
            }}
          >
            {STEPS.map((step, index) => (
              <Step key={step?.label} completed={index < activeStep}>
                <StepLabel
                  onClick={() => goToStep(index)}
                  sx={{
                    cursor: index <= activeStep ? "pointer" : "default",
                  }}
                >
                  {step?.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ mt: 3, mb: 4 }}>{renderStepContent(activeStep)}</Box>

          <Divider sx={{ my: 4, borderColor: "rgba(255,255,255,0.1)" }} />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.3)",
                "&:hover": { borderColor: "#00ffcc" },
              }}
              variant="outlined"
            >
              <ChevronRight sx={{ ml: "4px" }} />
              قبلی
            </Button>

            <Button
              onClick={handleNext}
              disabled={isSubmitting}
              variant="contained"
              sx={{
                bgcolor: "#fff",
                color: "#000",
                fontWeight: 700,
                "&:hover": { bgcolor: "#00ffcc" },
              }}
            >
              {activeStep === STEPS.length - 1
                ? isSubmitting
                  ? "در حال ارسال..."
                  : "ارسال درخواست استخدام"
                : "بعدی"}
              {isSubmitting && activeStep === STEPS.length - 1 && (
                <CircularProgress size={18} sx={{ color: "#000", ml: 1 }} />
              )}
              {activeStep !== STEPS.length - 1 && (
                <ChevronLeft sx={{ mr: "4px" }} />
              )}
            </Button>
          </Box>
        </Box>
      </Container>

      <VerificationModal
        open={isVerifyOpen}
        onClose={() => setIsVerifyOpen(false)}
        phoneNumber={userPhone}
        tmpToken={tmpToken}
        onSuccess={() => {
          formik.resetForm();
          navigate("/");
        }}
      />
    </Box>
  );
}
